import {Folio, FolioPayment, FolioStatus} from "crqlar-react-common/dist/models/billing/folio.model";
import {FolioService} from "crqlar-react-common/dist/services/billing/folio.service";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import {useAvailableBillingProviders} from "crqlar-react-common/dist/utils/hooks/billing/useAvailableBillingProviders";
import {useSnackbar} from "notistack";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery} from "react-query";

export const NO_CHARGE = 'no_charge';
const ROOM_CHARGE_PROVIDERS = ['asa', 'fias'];

interface Options {
    folio?: Folio
    /** Read-only users charge nothing: providers and stays aren't read. */
    enabled: boolean
    /** The charge went through (or nothing had to be charged): the folio as it is now. */
    onCharged: (folio: Folio) => void
    /** The charge failed: the folio stays open, as the backend returned it. */
    onFailed: (folio: Folio) => void
}

/** Why a provider refused the charge, from its data on the folio; the generic text otherwise. */
const chargeError = (provider: string, folio: Folio, t: (key: string) => string): string => {
    if (!folio.folioProviderData) return t('Charging failed');
    try {
        const data = JSON.parse(folio.folioProviderData);
        if (provider === 'asa' && data.success === false && data.error?.value) return data.error.value;
        if (provider === 'casablanca' && data.success === false && data.error) {
            // error is an i18n key (e.g. "casablanca.guest.not.checked.in"), message the concrete reason
            const reason = t(data.error) || data.error;
            return data.message ? `${reason} — ${data.message}` : reason;
        }
    } catch (e) {
        console.error('Failed to parse folioProviderData:', e);
    }
    return t('Charging failed');
};

/** The payment step: the providers, the one chosen and its data, and the charge itself. */
export const useFolioPayment = ({folio, enabled, onCharged, onFailed}: Options) => {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const providersQuery = useAvailableBillingProviders();
    const providers = [NO_CHARGE, ...(providersQuery.data || [])];
    const [provider, setProvider] = useState<string>(NO_CHARGE);
    const [providerData, setProviderData] = useState<any>(null);
    const [providerDataValid, setProviderDataValid] = useState(true);
    // A refresh starts the provider's form over: it reads what it shows again - the saved cards, the
    // guest's room, the in-house check - with the folio as it is now
    const [formKey, setFormKey] = useState(0);
    // Staff's pick of whom the charge bills among the folio's guests; undefined: the folio's bill-to
    const [billToPick, setBillToPick] = useState<string>();
    const billTo = billToPick ?? folio?.profileId;

    // Another folio starts without a choice
    useEffect(() => {
        setProvider(NO_CHARGE);
        setProviderData(null);
        setProviderDataValid(true);
        setBillToPick(undefined);
    }, [folio?.id]);

    // The room of whom the charge bills
    const roomCharge = ROOM_CHARGE_PROVIDERS.includes(provider.toLowerCase());
    const activeStayEnabled = enabled && roomCharge && !!billTo;
    const activeStayQuery = useQuery(
        ['activeStay', billTo],
        () => ProfileService.getActiveStay(billTo!),
        {enabled: activeStayEnabled, refetchOnWindowFocus: false}
    );
    const guestNotStaying = roomCharge && !!billTo && !activeStayQuery.isLoading
        && activeStayQuery.data?.hasActiveStay === false;

    const chargeMutation = useMutation(async () => {
        if (!folio?.id || provider === NO_CHARGE) return undefined;
        const total = await FolioService.calculateTotal({id: folio.id});
        const payment: FolioPayment = {
            provider,
            amount: total,
            transactionDate: new Date().toISOString().split('T')[0],
            paymentMethodId: providerData?.paymentMethodId,
            email: providerData?.email,
            firstName: providerData?.firstName,
            lastName: providerData?.lastName,
            sendEmail: providerData?.sendEmail,
            chargeImmediately: providerData?.chargeImmediately,
            roomId: providerData?.roomId,
            externalUserId: providerData?.externalUserId,
            externalReservationId: providerData?.externalReservationId,
            // A provider with a person of its own (Mews) posts to them; any other to whom the charge
            // bills - FIAS books to the payment's person, else the folio's
            profileId: providerData?.profileId ?? billTo,
            reservationId: providerData?.reservationId,
        };
        return FolioService.chargeFolio({id: folio.id, payment});
    }, {
        onSuccess: charged => {
            if (!charged) return;
            if (charged.status === FolioStatus.OPEN) {
                enqueueSnackbar(chargeError(provider, charged, t), {variant: 'error'});
                onFailed(charged);
            } else {
                enqueueSnackbar(t('Charge success'), {variant: 'success'});
                onCharged(charged);
            }
        },
        onError: error => {
            console.error('Error charging folio:', error);
            enqueueSnackbar(t('Charging failed'), {variant: 'error'});
        },
    });

    return {
        providers,
        providersLoading: providersQuery.isLoading,
        providersError: providersQuery.isError,
        provider,
        selectProvider: (next: string | null) => next && setProvider(next),
        onProviderData: (valid: boolean, data: any) => {
            setProviderDataValid(valid);
            setProviderData(data);
        },
        /** There is a provider to charge with, besides "No charge". */
        canChooseProvider: providers.length > 1,
        /** Whom the charge bills: staff's pick among the folio's guests, else the folio's bill-to. */
        billTo,
        selectBillTo: (profileId: string) => setBillToPick(profileId),
        guestNotStaying,
        /** Casablanca books VAT exactly as transmitted: then every line needs one. */
        vatRequired: providers.includes('casablanca'),
        canCharge: folio?.status === FolioStatus.OPEN && provider !== NO_CHARGE && providerDataValid && !guestNotStaying,
        charge: () => chargeMutation.mutate(),
        charging: chargeMutation.isLoading,
        /** Reads the providers and the guest's stay again, and starts the provider's form over; the chosen method stays. */
        refresh: async () => {
            if (!enabled) return;
            setFormKey(key => key + 1);
            await Promise.all([providersQuery.refetch(), activeStayEnabled ? activeStayQuery.refetch() : undefined]);
        },
        /** The provider's form's key: a refresh remounts it. */
        formKey,
    };
};

export type FolioPaymentState = ReturnType<typeof useFolioPayment>;
